import { PageLayoutWidgetDto } from "@lib/ShiOneClient";
import RankingWidget, {
  RankingWidgetContainerHeight,
} from "@widgets/standard/RankingWidget";
import React, { useMemo } from "react";
import { SortingOrder } from "../../../../../../components/mui-data-grid/constants";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";
import DevicesIcon from "@mui/icons-material/Devices";
import { NoContent, ShadedCellContent, SkeletonWrapper } from "shared-ui";
import useGetAssessmentCapabilityChanges from "@features/assessments-feature/hooks/useGetAssessmentCapabilityChanges";
import { FormControl, MenuItem, Select, Typography } from "@mui/material";
import theme from "../../../../../../theme";
import { maturityLevelColors } from "@features/assessments-feature/utils/assessmentsColors";
import {
  ArrowDownward,
  ArrowUpward,
  ChevronRightOutlined,
} from "@mui/icons-material";
import { getMaturityIcon } from "@features/assessments-feature/helpers/assessmentMaturityChartHelpers";
import { useAssessmentDashboardContext } from "@features/assessments-feature/contexts-providers/AssessmentDashboardContextProvider";
import { determineProgram } from "@features/assessments-feature/helpers/assessmentWidgetHelpers";

const numberFormatter = new Intl.NumberFormat("en-US");

type Capability = {
  id: number;
  capability: string;
  maturity: string;
  originalMaturity: string;
  newMaturity: string;
};

type TransformedCapability = {
  id: number;
  capability: string;
  difference: number;
  originalMaturity: string;
  newMaturity: string;
};

const capabilityChangesfilters = [
  { label: "First to Current Assessment", value: "firstToCurrent" },
  { label: "Previous to Current Assessment", value: "prevToCurrent" },
];

const getMaturityDelta = (
  oldest: Capability[],
  newest: Capability[],
): TransformedCapability[] => {
  return oldest.reduce((acc, oldestItem, index) => {
    const newestItem = newest.find(
      (item) => item.capability === oldestItem.capability,
    );
    if (newestItem) {
      const oldestMaturityNumber = parseInt(
        oldestItem.maturity.split(" - ")[0],
      );
      const newestMaturityNumber = parseInt(
        newestItem.maturity.split(" - ")[0],
      );
      const maturityDelta = newestMaturityNumber - oldestMaturityNumber;
      if (maturityDelta !== 0) {
        acc.push({
          id: index,
          capability: oldestItem.capability,
          difference: maturityDelta,
          originalMaturity: oldestItem.maturity.split(" - ")[1].split(" / ")[0], //Some strings are "3 - Evaluating / Changed", and we only want Evaluating
          newMaturity: newestItem.maturity.split(" - ")[1].split(" / ")[0],
        });
      }
    }
    return acc;
  }, [] as TransformedCapability[]);
};

const getGridDefinitions = (loading: boolean, error: boolean) => {
  return {
    sortedColumns: [{ field: "id", sort: SortingOrder.Descending }],
    columnDefinitions: [
      {
        field: "capability",
        headerName: "Capability",
        minWidth: 150,
        flex: 1,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <SkeletonWrapper loading={loading || error}>
              {params.value}
            </SkeletonWrapper>
          );
        },
      },
      {
        field: "originalMaturity",
        headerName: "Change",
        minWidth: 150,
        flex: 1,
        renderCell: (params: GridRenderCellParams) => {
          const maturityIconStyle = {
            marginRight: "4px",
            width: "16px",
            height: "16px",
            marginBottom: "4px",
          };

          return (
            <SkeletonWrapper loading={loading || error}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {getMaturityIcon(params.value ?? "", maturityIconStyle)}
                <span style={{ color: maturityLevelColors[params.value] }}>
                  {params.value}
                </span>
                <ChevronRightOutlined sx={{ fontSize: 18, mx: 1 }} />
                {getMaturityIcon(
                  params.row.newMaturity ?? "",
                  maturityIconStyle,
                )}
                <span
                  style={{ color: maturityLevelColors[params.row.newMaturity] }}
                >
                  {params.row.newMaturity}
                </span>
              </span>
            </SkeletonWrapper>
          );
        },
      },
      {
        field: "difference",
        headerName: "Maturity Delta",
        minWidth: 50,
        renderCell: (params: GridRenderCellParams) => {
          const totalRows = params.api.getRowsCount();
          const value =
            typeof params?.value === "number"
              ? numberFormatter.format(params?.value)
              : params?.value;
          const red = theme.palette.red;
          const green = theme.palette.green;
          const isPositiveValue = params.value > 0;
          return (
            <SkeletonWrapper loading={loading || error}>
              <ShadedCellContent
                index={params.row.id}
                value={
                  <>
                    {isPositiveValue ? (
                      <>
                        <ArrowUpward
                          sx={{
                            color: "#2E7D32",
                            fontSize: "14px",
                            paddingRight: "2px",
                          }}
                        />
                        {value}
                      </>
                    ) : (
                      <>
                        <ArrowDownward
                          sx={{
                            color: `rgb(${red})`,
                            fontSize: "14px",
                            paddingRight: "2px",
                          }}
                        />
                        {Math.abs(value)}
                      </>
                    )}
                  </>
                }
                numberOfRows={totalRows}
                rgbNumber={isPositiveValue ? green : red}
                colorFadeout={false}
                additionalStyles={{ justifyContent: "left" }}
              />
            </SkeletonWrapper>
          );
        },
      },
    ],
  };
};

export const CardActionAssessmentComponent = ({
  assessmentPosition,
  date,
}: {
  assessmentPosition?: string;
  date?: Date;
}) => {
  if (!date || !assessmentPosition) return;
  const formattedDate = date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
  return (
    <div>
      <Typography variant={"caption"} color="textSecondary" sx={{ m: 1 }}>
        {assessmentPosition} assessment was completed on {formattedDate}
      </Typography>
    </div>
  );
};

export const CapabilityChangesReusableComponent = ({programString, widgetNoSubheader, height}: {programString:string, widgetNoSubheader: PageLayoutWidgetDto; height?: string }) => {
  const [filter, setFilter] = React.useState(capabilityChangesfilters[0].value);

  const program = useMemo(
    () => determineProgram(programString),
    [programString],
  );
  const { data, isLoading, isError } =
    useGetAssessmentCapabilityChanges(program);


  const sortedData = useMemo(() => {
    if (!data || data.length < 2) return [];

    return [...data].sort((a, b) => {
      const aDate = a?.date?.getTime() ?? 0;
      const bDate = b?.date?.getTime() ?? 0;
      return bDate - aDate;
    });
  }, [data]);

  const capabilityChangesData = useMemo(() => {
    if (sortedData?.length < 2 || isLoading || isError) return [];
    const [current, previous] = sortedData;
    if (filter === "prevToCurrent") {
      return getMaturityDelta(
        previous.capabilityChangesScoresList ?? [],
        current.capabilityChangesScoresList ?? [],
      );
    } else {
      const firstAssessmentIndex = sortedData.length - 1;
      return getMaturityDelta(
        sortedData[firstAssessmentIndex].capabilityChangesScoresList ?? [],
        current.capabilityChangesScoresList ?? [],
      );
    }
  }, [sortedData, isLoading, isError, filter]);

  return (
    <RankingWidget
      pageLayoutWidget={widgetNoSubheader ?? {}}
      data={capabilityChangesData ?? []}
      gridDefinitions={getGridDefinitions(isLoading, isError)}
      getRowId={(row) => row.id}
      rowExpandLookup={undefined}
      height={height}
      cardActions={
        <CardActionAssessmentComponent
          assessmentPosition={filter === "prevToCurrent" ? "Previous" : "First"}
          date={
            filter === "prevToCurrent"
              ? sortedData[1]?.date
              : sortedData[sortedData.length - 1]?.date
          }
        />
      }
      headerAction={
        <FormControl sx={{ marginLeft: 1, flex: "auto" }} size="small">
          <Select
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          >
            {capabilityChangesfilters.map((filter) => (
              <MenuItem key={filter.value} value={filter.value}>
                {filter.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      }
      noDataElement={
        <NoContent
          header={"No Results"}
          body={"There are no changes to show between the filtered assessments"}
          actionText={""}
          icon={<DevicesIcon />}
          containerHeight={RankingWidgetContainerHeight}
        />
      }
      loading={isLoading}
    />);
}

const CapabilityChangesWidget = ({
  pageLayoutWidget,
}: {
  pageLayoutWidget: PageLayoutWidgetDto;
}) => {
  const widgetNoSubheader = pageLayoutWidget;
  widgetNoSubheader.widgetGroup = "";

  const { program: programString } = useAssessmentDashboardContext();

  return (
    <CapabilityChangesReusableComponent 
      programString={programString}
      widgetNoSubheader={widgetNoSubheader}
    />
  )
};

export default CapabilityChangesWidget;
